import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from '../../utils/axios';
import { AxiosResponse } from 'axios';
import { API_URL } from '../../constants';
import { StripedDataGrid } from '../../styledComponents/datagrid';
import { renderCellExpand } from '../../components/library/DataGrid/renderCellExpand';
import {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@material-ui/icons/Search';
import { PageWrapper } from '../../styledComponents/wrappers';
import { FormatDateString } from '../../utils/helper';

const PAGE_SIZE = 15;

export const FULFILLMENT_ORDERS_ROUTE = '/fulfillment/orders';

type WaveStatus = 'no_wave' | 'in_wave' | 'exception';
type OrderType = 'bike_only' | 'paa_only' | 'mixed' | 'unknown';

interface FulfillmentOrderSummary {
  shopifyOrderId: number;
  shopifyOrderGid: string;
  shopifyOrderName: string;
  createdAt: string;
  customerName: string;
  lineItemCount: number;
  orderType: OrderType;
  waveStatus: WaveStatus;
  waveId: number | null;
  lifecycleStatus: string | null;
  isMixed: boolean;
}

const ORDER_TYPE_LABELS: Record<OrderType, string> = {
  bike_only: 'Bike only',
  paa_only: 'PA&A only',
  mixed: 'Mixed',
  unknown: '—',
};

const WaveStatusChip: React.FC<{ status: WaveStatus; waveId: number | null }> = ({
  status,
  waveId,
}) => {
  if (status === 'exception') {
    return <Chip label="Exception" size="small" color="error" />;
  }
  if (status === 'in_wave') {
    return <Chip label={`Wave #${waveId}`} size="small" color="primary" />;
  }
  return <Chip label="No wave" size="small" />;
};

const columns: GridColDef[] = [
  {
    headerName: 'Order #',
    field: 'shopifyOrderName',
    flex: 0.7,
    renderCell: renderCellExpand,
  },
  {
    headerName: 'Customer',
    field: 'customerName',
    flex: 1,
    renderCell: renderCellExpand,
  },
  {
    headerName: 'Items',
    field: 'lineItemCount',
    flex: 0.4,
    type: 'number',
  },
  {
    headerName: 'Order type',
    field: 'orderType',
    flex: 0.8,
    valueFormatter: ({ value }) => ORDER_TYPE_LABELS[value as OrderType] ?? value,
  },
  {
    headerName: 'Status',
    field: 'waveStatus',
    flex: 1,
    renderCell: (params: GridRenderCellParams<FulfillmentOrderSummary>) => (
      <WaveStatusChip status={params.row.waveStatus} waveId={params.row.waveId} />
    ),
  },
  {
    headerName: 'Order date',
    field: 'createdAt',
    flex: 0.8,
    valueFormatter: ({ value }) => (value ? FormatDateString(value) : ''),
  },
];

const FulfillmentOrdersList: React.FC = () => {
  const history = useHistory();
  const [orders, setOrders] = useState<FulfillmentOrderSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PAGE_SIZE,
  });

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API_URL}/fulfillment/orders`);
        setOrders((response as AxiosResponse<FulfillmentOrderSummary[]>).data);
      } catch {
        setError('Failed to load fulfillment orders.');
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const filteredOrders = useMemo(() => {
    if (!searchTerm.trim()) return orders;
    const term = searchTerm.toLowerCase();
    return orders.filter(
      o =>
        o.shopifyOrderName.toLowerCase().includes(term) ||
        o.customerName.toLowerCase().includes(term),
    );
  }, [orders, searchTerm]);

  const handleRowClick = (params: GridRowParams) => {
    history.push(`/fulfillment/orders/${(params.row as FulfillmentOrderSummary).shopifyOrderId}`);
  };

  return (
    <PageWrapper>
      <Typography variant="h5" gutterBottom>
        Fulfillment Orders
      </Typography>
      <Box mb={2}>
        <TextField
          size="small"
          placeholder="Search by order # or customer"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          style={{ width: 320 }}
        />
      </Box>
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <StripedDataGrid
          rows={filteredOrders}
          columns={columns}
          getRowId={row => row.shopifyOrderId}
          onRowClick={handleRowClick}
          autoHeight
          disableRowSelectionOnClick
          paginationModel={paginationModel}
          onPaginationModelChange={model => setPaginationModel(model)}
          pageSizeOptions={[PAGE_SIZE, PAGE_SIZE * 2, PAGE_SIZE * 4]}
          initialState={{
            sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] },
            pagination: { paginationModel: { pageSize: PAGE_SIZE } },
          }}
          sx={{ cursor: 'pointer' }}
        />
      )}
    </PageWrapper>
  );
};

export default FulfillmentOrdersList;
